/**
 * Conversational Onboarding Context
 * -----------------------------------
 * State management for the chat-based onboarding flow.
 * Handles session state, messages, and slot extraction.
 */

import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Toaster } from '../components/common';
import { routes } from '../lib/routes';
import {
  startConversationalOnboarding,
  sendOnboardingMessage,
  completeConversationalOnboarding,
  type ConversationalChatResponse,
  type ExtractedSlot,
} from '../services/conversationalOnboardingService';
import { useAuth } from './AuthContext';
import { useBranding } from './BrandingContext';

// Session storage key base names - will be scoped to user ID for data isolation
const STORAGE_KEY_SESSION_ID = 'onboarding_session_id';
const STORAGE_KEY_MESSAGES = 'onboarding_messages';
const STORAGE_KEY_PROGRESS = 'onboarding_progress';
const STORAGE_KEY_SLOTS = 'onboarding_slots';
const STORAGE_KEY_JUST_COMPLETED = 'onboarding_just_completed';

// Helper to create user-scoped storage keys (CRITICAL for data isolation)
const getScopedKey = (baseKey: string, userId: string | undefined): string => {
  if (!userId) return baseKey; // Fallback for edge cases
  return `${baseKey}_${userId}`;
};

// Timeout configuration for AI responses (60 seconds)
// INCREASED from 30s - LLM-based slot extraction can take 30-40s on cold starts
const AI_RESPONSE_TIMEOUT_MS = 60000;

// Retry configuration for transient failures (e.g., Render cold starts)
// REDUCED from 2 to 1 - aggressive retries cause duplicate processing (Sentry 7311102382)
const MAX_RETRIES = 1;
const RETRY_DELAY_MS = 3000;

// Deduplication: track last message to prevent duplicate submissions
let lastMessageHash = '';
let lastMessageTime = 0;
const DEDUP_WINDOW_MS = 5000; // 5 second window

// Helper to create a timeout promise
const createTimeoutPromise = (ms: number): Promise<never> =>
  new Promise((_, reject) =>
    setTimeout(() => reject(new Error('AI response timed out. Please try again.')), ms)
  );

// Helper for retry with exponential backoff
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Message type for chat UI
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  extractedSlots?: Record<string, ExtractedSlot>;
}

interface ConversationalOnboardingContextType {
  // Session state
  sessionId: string | null;
  isLoading: boolean;
  isSending: boolean;
  isProcessingProfile: boolean;

  // Messages
  messages: ChatMessage[];

  // Progress
  completionPercent: number;
  phase: string;
  isComplete: boolean;
  suggestedQuestions: string[];

  // Slots
  allSlots: Record<string, ExtractedSlot>;

  // Error state for timeout/retry UI
  sendError: string | null;
  lastFailedMessage: string | null;

  // Completion lock - prevents state regression after user confirms
  completionLocked: boolean;

  // Actions
  startSession: (
    objective?: string,
    opts?: { forceNew?: boolean; eventName?: string | null }
  ) => Promise<void>;
  sendMessage: (message: string) => Promise<void>;
  completeOnboarding: (slotOverrides?: Record<string, string>) => Promise<void>;
  lockCompletion: () => void;
  retryLastMessage: () => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

const ConversationalOnboardingContext = createContext<ConversationalOnboardingContextType | undefined>(undefined);

interface ConversationalOnboardingProviderProps {
  children: React.ReactNode;
}

export const ConversationalOnboardingProvider: React.FC<ConversationalOnboardingProviderProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const { user, checkStatus } = useAuth();
  const userId = user?.id;
  // V1.5.M12: white-label organizer name so the greeting reads "Linc from <Organizer>".
  const { name: brandName, isDefault: brandIsDefault } = useBranding();

  // Track previous user ID to detect user changes (CRITICAL for data isolation)
  const prevUserIdRef = useRef<string | undefined>(userId);

  // Initialize state from sessionStorage for persistence across navigation
  // IMPORTANT: All storage keys are scoped to user ID to prevent data leakage
  const [sessionId, setSessionId] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(getScopedKey(STORAGE_KEY_SESSION_ID, userId));
    } catch {
      return null;
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isProcessingProfile, setIsProcessingProfile] = useState(false);

  // Error state for timeout/retry functionality
  const [sendError, setSendError] = useState<string | null>(null);
  const [lastFailedMessage, setLastFailedMessage] = useState<string | null>(null);

  // CRITICAL: Completion lock - once user confirms profile summary, prevent state regression
  // This fixes the bug where delayed sendMessage responses reset progress/isComplete
  const [completionLocked, setCompletionLocked] = useState(false);
  // BUG-008 FIX: Use ref to avoid stale closure bug in sendMessage callback
  // The state value can be stale if sendMessage was defined before lockCompletion was called
  const completionLockedRef = useRef(false);
  // QA fix (Jun-29, event review): synchronous re-entrancy guard for startSession.
  // isLoading/sessionId are React state (updated async), so rapid re-fires (StrictMode
  // double-invoke, re-renders before state propagates) slipped past the state guards
  // and each call hit the start API, creating duplicate greeting turns (Fabiana got 7).
  // A ref blocks synchronously. Reset only on failure (to allow retry) and on reset.
  const startedRef = useRef(false);

  // Messages - restore from sessionStorage (scoped to user)
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    try {
      const stored = sessionStorage.getItem(getScopedKey(STORAGE_KEY_MESSAGES, userId));
      if (stored) {
        const parsed = JSON.parse(stored);
        // Restore Date objects
        return parsed.map((msg: ChatMessage) => ({
          ...msg,
          timestamp: new Date(msg.timestamp),
        }));
      }
    } catch {
      // Ignore parse errors
    }
    return [];
  });

  // Progress - restore from sessionStorage (scoped to user)
  const [completionPercent, setCompletionPercent] = useState(() => {
    try {
      const stored = sessionStorage.getItem(getScopedKey(STORAGE_KEY_PROGRESS, userId));
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.completionPercent || 0;
      }
    } catch {
      // Ignore parse errors
    }
    return 0;
  });
  const [phase, setPhase] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);

  // Slots - restore from sessionStorage (scoped to user)
  const [allSlots, setAllSlots] = useState<Record<string, ExtractedSlot>>(() => {
    try {
      const stored = sessionStorage.getItem(getScopedKey(STORAGE_KEY_SLOTS, userId));
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {
      // Ignore parse errors
    }
    return {};
  });

  // CRITICAL: Reset state when user changes to prevent data leakage between accounts
  useEffect(() => {
    if (prevUserIdRef.current !== userId && prevUserIdRef.current !== undefined) {
      console.log('[Onboarding] User changed, clearing previous session data');
      // Clear all state for new user
      setSessionId(null);
      setMessages([]);
      setCompletionPercent(0);
      setPhase('');
      setIsComplete(false);
      setSuggestedQuestions([]);
      setAllSlots({});
      setSendError(null);
      setLastFailedMessage(null);

      // Try to restore new user's data from storage
      if (userId) {
        try {
          const storedSessionId = sessionStorage.getItem(getScopedKey(STORAGE_KEY_SESSION_ID, userId));
          if (storedSessionId) setSessionId(storedSessionId);

          const storedMessages = sessionStorage.getItem(getScopedKey(STORAGE_KEY_MESSAGES, userId));
          if (storedMessages) {
            const parsed = JSON.parse(storedMessages);
            setMessages(parsed.map((msg: ChatMessage) => ({
              ...msg,
              timestamp: new Date(msg.timestamp),
            })));
          }

          const storedProgress = sessionStorage.getItem(getScopedKey(STORAGE_KEY_PROGRESS, userId));
          if (storedProgress) {
            const parsed = JSON.parse(storedProgress);
            setCompletionPercent(parsed.completionPercent || 0);
          }

          const storedSlots = sessionStorage.getItem(getScopedKey(STORAGE_KEY_SLOTS, userId));
          if (storedSlots) setAllSlots(JSON.parse(storedSlots));
        } catch {
          // Ignore parse errors
        }
      }
    }
    prevUserIdRef.current = userId;
  }, [userId]);

  // Persist session ID to sessionStorage (scoped to user)
  useEffect(() => {
    if (sessionId && userId) {
      sessionStorage.setItem(getScopedKey(STORAGE_KEY_SESSION_ID, userId), sessionId);
    }
  }, [sessionId, userId]);

  // Persist messages to sessionStorage (scoped to user)
  useEffect(() => {
    if (messages.length > 0 && userId) {
      sessionStorage.setItem(getScopedKey(STORAGE_KEY_MESSAGES, userId), JSON.stringify(messages));
    }
  }, [messages, userId]);

  // Persist progress to sessionStorage (scoped to user)
  useEffect(() => {
    if (userId) {
      sessionStorage.setItem(getScopedKey(STORAGE_KEY_PROGRESS, userId), JSON.stringify({ completionPercent }));
    }
  }, [completionPercent, userId]);

  // Persist slots to sessionStorage (scoped to user)
  useEffect(() => {
    if (Object.keys(allSlots).length > 0 && userId) {
      sessionStorage.setItem(getScopedKey(STORAGE_KEY_SLOTS, userId), JSON.stringify(allSlots));
    }
  }, [allSlots, userId]);

  // Generate unique message ID
  const generateMessageId = () => `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  /**
   * Start a new onboarding session
   */
  const startSession = useCallback(async (
    objective?: string,
    opts?: { forceNew?: boolean; eventName?: string | null }
  ) => {
    // CRITICAL: Guard against race conditions
    // 0. Synchronous re-entrancy guard (QA fix Jun-29): blocks duplicate greeting
    //    turns (Fabiana x7) that the async state guards below let through on rapid
    //    re-fires. Set before any await; reset only on failure / resetOnboarding.
    //    startedRef still blocks a genuine re-fire on a forceNew re-run; only the
    //    sessionId half is bypassed, because the reonboard caller resets state and
    //    then starts in the same tick, so this closure can still see the old id.
    if (startedRef.current || (sessionId && !opts?.forceNew)) {
      console.log('[Onboarding] Skipping startSession - already started/in flight');
      return;
    }
    startedRef.current = true;
    // 1. Don't start if already loading (prevents duplicate concurrent calls)
    if (isLoading) {
      console.log('[Onboarding] Skipping startSession - already loading');
      return;
    }

    // 2. Don't start if session already exists. A forceNew re-run is exempt for
    //    the same reason as the guard above: the caller resets and starts in the
    //    same tick, so this closure still sees the pre-reset id and would bail.
    if (sessionId && !opts?.forceNew) {
      console.log('[Onboarding] Skipping startSession - session already exists:', sessionId);
      return;
    }

    // 3. Don't start if just completed (prevents post-completion race)
    const justCompleted = sessionStorage.getItem(getScopedKey(STORAGE_KEY_JUST_COMPLETED, userId));
    if (justCompleted) {
      console.log('[Onboarding] Skipping startSession - just completed onboarding');
      return;
    }

    setIsLoading(true);
    try {
      const response = await startConversationalOnboarding({
        objective,
        organizer_name: brandIsDefault ? undefined : brandName,
        force_new: opts?.forceNew || undefined,
        event_name: opts?.eventName || undefined,
      });

      if (response.code !== 200 || !response.result) {
        toast.custom(t => (
          <Toaster
            message={response.message || 'Failed to start session'}
            type='error'
            id={t.id}
          />
        ));
        return;
      }

      const { session_id, greeting, suggested_questions, progress_percent, history } = response.result;

      setSessionId(session_id);
      setCompletionPercent(progress_percent);
      setSuggestedQuestions(suggested_questions);

      // On resume, replay the prior conversation so the user sees where they left
      // off, then show the "welcome back" greeting as the latest message. For a
      // brand-new user, history is empty and only the greeting renders.
      const priorMessages: ChatMessage[] = Array.isArray(history)
        ? history
            .filter(h => h && h.content)
            .map(h => ({
              id: generateMessageId(),
              role: h.role === 'assistant' ? 'assistant' : 'user',
              content: h.content,
              timestamp: new Date(),
            }))
        : [];

      setMessages([
        ...priorMessages,
        {
          id: generateMessageId(),
          role: 'assistant',
          content: greeting,
          timestamp: new Date(),
        },
      ]);

    } catch (error) {
      // Allow a retry after a genuine failure (the re-entrancy guard is only meant
      // to stop duplicate concurrent/rapid starts, not to block recovery).
      startedRef.current = false;
      console.error('Failed to start session:', error);
      toast.custom(t => (
        <Toaster message='Failed to start onboarding session' type='error' id={t.id} />
      ));
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, sessionId, userId, brandName, brandIsDefault]);

  /**
   * Send a message in the conversation with timeout and retry support
   */
  const sendMessage = useCallback(async (message: string) => {
    if (!message.trim()) return;

    // DEDUPLICATION: Prevent duplicate submissions (Sentry 7311102382)
    // Skip if same message sent within dedup window
    const messageHash = message.trim().toLowerCase();
    const now = Date.now();
    if (messageHash === lastMessageHash && now - lastMessageTime < DEDUP_WINDOW_MS) {
      console.log('[Onboarding] Duplicate message detected, skipping');
      return;
    }
    lastMessageHash = messageHash;
    lastMessageTime = now;

    // Clear any previous error state
    setSendError(null);
    setLastFailedMessage(null);
    setIsSending(true);

    // Add user message immediately
    const userMessage: ChatMessage = {
      id: generateMessageId(),
      role: 'user',
      content: message,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      // Retry loop for transient failures (e.g., Render cold starts)
      let response;
      let lastError: Error | null = null;

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          // Race between the API call and timeout
          response = await Promise.race([
            sendOnboardingMessage({
              message,
              session_id: sessionId || undefined,
            }),
            createTimeoutPromise(AI_RESPONSE_TIMEOUT_MS),
          ]);

          // If we got a successful response, break out of retry loop
          if (response.code === 200 && response.result) {
            break;
          }

          // If it's a server error (5xx), retry
          if (response.code >= 500 && attempt < MAX_RETRIES) {
            console.log(`[Onboarding] Retry ${attempt + 1}/${MAX_RETRIES} after ${response.code} error`);
            await sleep(RETRY_DELAY_MS * (attempt + 1)); // Exponential backoff
            continue;
          }

          // Non-retryable error, break
          break;
        } catch (err) {
          lastError = err as Error;
          // Timeout or network error - retry if attempts remain
          if (attempt < MAX_RETRIES) {
            console.log(`[Onboarding] Retry ${attempt + 1}/${MAX_RETRIES} after error:`, err);
            await sleep(RETRY_DELAY_MS * (attempt + 1));
            continue;
          }
          throw err;
        }
      }

      if (!response || response.code !== 200 || !response.result) {
        // API returned error - set error state for retry UI
        const errorMsg = response?.message || lastError?.message || 'Failed to send message';
        setSendError(errorMsg);
        setLastFailedMessage(message);
        toast.custom(t => (
          <Toaster message={errorMsg} type='error' id={t.id} />
        ));
        return;
      }

      const result: ConversationalChatResponse = response.result;

      // Update session ID if new
      if (!sessionId && result.session_id) {
        setSessionId(result.session_id);
      }

      // Update progress FIRST (before showing next question)
      // UX fix: Progress bar should update immediately after user's answer is processed,
      // not after the AI's next question appears
      //
      // CRITICAL: If completionLocked, don't update state - prevents regression bug
      // where delayed responses reset progress after user confirmed profile summary
      // BUG-008 FIX: Check ref instead of state to avoid stale closure issue
      // When this callback was created, completionLocked may have been false,
      // but lockCompletion() may have been called while the request was in-flight
      console.log('[Onboarding] API response received:', {
        is_complete: result.is_complete,
        completion_percent: result.completion_percent,
        completionLockedRef: completionLockedRef.current
      });
      if (!completionLockedRef.current) {
        console.log('[Onboarding] ✅ Setting isComplete to:', result.is_complete);
        setCompletionPercent(result.completion_percent);
        setPhase(result.phase);
        setIsComplete(result.is_complete);
        setSuggestedQuestions(result.next_questions);
        setAllSlots(result.all_slots);
      } else {
        console.log('[Onboarding] ❌ State update BLOCKED - completion is locked');
      }

      // THEN add assistant response (next question)
      const assistantMessage: ChatMessage = {
        id: generateMessageId(),
        role: 'assistant',
        content: result.ai_response,
        timestamp: new Date(),
        extractedSlots: result.extracted_slots,
      };
      setMessages(prev => [...prev, assistantMessage]);

      // If complete, show toast - user will click "Complete & View Matches" button
      // to finalize (no auto-complete to avoid racing with user confirmation flow)
      if (result.is_complete) {
        toast.custom(t => (
          <Toaster
            message='Great! Your profile information is complete. Review and confirm to continue.'
            type='success'
            id={t.id}
          />
        ));
        // Don't auto-complete - let user review summary and click button
        // This prevents the overlay from blocking the confirmation modal
      }

    } catch (error) {
      // Handle timeout or network errors
      console.error('Failed to send message:', error);
      const errorMsg = error instanceof Error ? error.message : 'Failed to send message. Please try again.';
      setSendError(errorMsg);
      setLastFailedMessage(message);
      toast.custom(t => (
        <Toaster message={errorMsg} type='error' id={t.id} />
      ));
    } finally {
      setIsSending(false);
    }
  }, [sessionId, userId, completionLocked]);

  /**
   * Retry the last failed message
   */
  const retryLastMessage = useCallback(async () => {
    if (lastFailedMessage) {
      // Remove the last user message that failed (it will be re-added by sendMessage)
      setMessages(prev => prev.slice(0, -1));
      await sendMessage(lastFailedMessage);
    }
  }, [lastFailedMessage, sendMessage]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setSendError(null);
    setLastFailedMessage(null);
  }, []);

  /**
   * Lock completion state - prevents regression from delayed API responses
   * Call this when user confirms the profile summary modal
   */
  const lockCompletion = useCallback(() => {
    console.log('[Onboarding] Completion locked - state updates will be ignored');
    // BUG-008 FIX: Set ref immediately for in-flight requests (avoids stale closure)
    completionLockedRef.current = true;
    setCompletionLocked(true);
    // Also ensure isComplete stays true
    setIsComplete(true);
  }, []);

  /**
   * Complete onboarding and create profile
   */
  const completeOnboarding = useCallback(async (slotOverrides?: Record<string, string>) => {
    if (!sessionId) {
      toast.custom(t => (
        <Toaster message='No active session' type='error' id={t.id} />
      ));
      return;
    }

    // GUARD: Prevent duplicate completion calls (Sentry 7311102382)
    // If already processing, ignore additional clicks
    if (isLoading || isProcessingProfile) {
      console.log('[Onboarding] Completion already in progress, ignoring duplicate call');
      return;
    }

    setIsLoading(true);
    setIsProcessingProfile(true); // Show overlay while creating profile
    try {
      // Apr-23: if the user edited any slots on the review modal, send them
      // through as slot_overrides so the AI service merges them onto the
      // extracted slot_summary before persona generation. Matches the mobile
      // edit flow shipped today (commit 490f413). Empty/missing overrides are
      // omitted from the payload to keep the no-edit path byte-identical to
      // pre-fix behavior.
      const requestBody: { session_id: string; slot_overrides?: Record<string, string>; host?: string } = {
        session_id: sessionId,
        // Jun-20: send the current host so the backend can auto-enrol the attendee
        // into the white-label event (CloudFront does not forward Origin to the origin).
        host: window.location.hostname,
      };
      if (slotOverrides && Object.keys(slotOverrides).length > 0) {
        requestBody.slot_overrides = slotOverrides;
      }
      const response = await completeConversationalOnboarding(requestBody);

      if (response.code !== 200 || !response.result) {
        toast.custom(t => (
          <Toaster
            message={response.message || 'Failed to complete onboarding'}
            type='error'
            id={t.id}
          />
        ));
        return;
      }

      toast.custom(t => (
        <Toaster
          message='Profile created successfully!'
          type='success'
          id={t.id}
        />
      ));

      // BUG FIX: Refresh user object to get updated onboarding_status = 'completed'
      // This prevents the onboarding loop bug where the route guard sees stale status
      await checkStatus();

      // CRITICAL: Set completion flag BEFORE clearing session
      // This prevents the reset bug where the page re-calls startSession()
      sessionStorage.setItem(getScopedKey(STORAGE_KEY_JUST_COMPLETED, userId), 'true');

      // REC-487: Set flag to show "Finding matches..." state on dashboard
      // Background tasks (embeddings, matching) are running, matches will appear in ~60s
      sessionStorage.setItem('finding_matches_started_at', Date.now().toString());

      // Clear persisted session after successful completion (scoped to user)
      sessionStorage.removeItem(getScopedKey(STORAGE_KEY_SESSION_ID, userId));
      sessionStorage.removeItem(getScopedKey(STORAGE_KEY_MESSAGES, userId));
      sessionStorage.removeItem(getScopedKey(STORAGE_KEY_PROGRESS, userId));
      sessionStorage.removeItem(getScopedKey(STORAGE_KEY_SLOTS, userId));

      // May-01 M3 A3: route through the AI Summary review page (which
      // already exists at /onboarding/ai-summary — Apr-21 CF-4) instead
      // of going straight to dashboard. User reviews + optionally edits
      // the rich persona narrative, then clicks "Find matches" / "Go to
      // dashboard" to proceed. Mirrors the new mobile flow shipped in
      // commit 4a54492 (persona-review.tsx). Doesn't introduce new
      // surfaces — just connects existing Apr-21 page into the active
      // flow. Edit-to-matching is real now (commit 17d4dda fixed the
      // gap where summary edits didn't propagate to embeddings).
      navigate(routes.onboardingAISummary);

    } catch (error) {
      console.error('Failed to complete onboarding:', error);

      // IMPROVED ERROR HANDLING: Show more specific error messages
      let errorMessage = 'Failed to complete onboarding. Please try again.';

      if (error instanceof Error) {
        if (error.message.includes('timeout') || error.message.includes('timed out')) {
          errorMessage = 'Request timed out. Your profile may still be processing - please wait and refresh.';
        } else if (error.message.includes('network') || error.message.includes('Network')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        }
      }

      // Check if error has a response with a message
      const apiError = error as { message?: string; response?: { data?: { message?: string } } };
      if (apiError?.response?.data?.message) {
        errorMessage = apiError.response.data.message;
      } else if (apiError?.message && typeof apiError.message === 'string') {
        errorMessage = apiError.message;
      }

      toast.custom(t => (
        <Toaster message={errorMessage} type='error' id={t.id} />
      ));
    } finally {
      setIsLoading(false);
      setIsProcessingProfile(false); // Clear overlay on completion or error
    }
  }, [sessionId, navigate, userId, checkStatus, isLoading, isProcessingProfile]);

  /**
   * Reset the context state and clear persisted data
   * Call this only when onboarding is complete, not on navigation
   */
  const reset = useCallback(() => {
    // Clear sessionStorage (scoped to user for data isolation)
    sessionStorage.removeItem(getScopedKey(STORAGE_KEY_SESSION_ID, userId));
    sessionStorage.removeItem(getScopedKey(STORAGE_KEY_MESSAGES, userId));
    sessionStorage.removeItem(getScopedKey(STORAGE_KEY_PROGRESS, userId));
    sessionStorage.removeItem(getScopedKey(STORAGE_KEY_SLOTS, userId));
    sessionStorage.removeItem(getScopedKey(STORAGE_KEY_JUST_COMPLETED, userId));

    // Reset state
    setSessionId(null);
    setMessages([]);
    setCompletionPercent(0);
    setPhase('');
    setIsComplete(false);
    setSuggestedQuestions([]);
    setAllSlots({});
    setIsLoading(false);
    setIsSending(false);
    setIsProcessingProfile(false);
    setSendError(null);
    setLastFailedMessage(null);
    setCompletionLocked(false);
    // BUG-008 FIX: Also reset the ref
    completionLockedRef.current = false;
    // QA fix (Jun-29): clear the start guard so a fresh session can begin after reset.
    startedRef.current = false;
  }, [userId]);

  const value = useMemo(
    () => ({
      sessionId,
      isLoading,
      isSending,
      isProcessingProfile,
      messages,
      completionPercent,
      phase,
      isComplete,
      suggestedQuestions,
      allSlots,
      sendError,
      lastFailedMessage,
      completionLocked,
      startSession,
      sendMessage,
      completeOnboarding,
      lockCompletion,
      retryLastMessage,
      clearError,
      reset,
    }),
    [
      sessionId,
      isLoading,
      isSending,
      isProcessingProfile,
      messages,
      completionPercent,
      phase,
      isComplete,
      suggestedQuestions,
      allSlots,
      sendError,
      lastFailedMessage,
      completionLocked,
      startSession,
      sendMessage,
      completeOnboarding,
      lockCompletion,
      retryLastMessage,
      clearError,
      reset,
    ]
  );

  return (
    <ConversationalOnboardingContext.Provider value={value}>
      {children}
    </ConversationalOnboardingContext.Provider>
  );
};

export const useConversationalOnboarding = (): ConversationalOnboardingContextType => {
  const context = useContext(ConversationalOnboardingContext);

  if (context === undefined) {
    throw new Error(
      'useConversationalOnboarding must be used within a ConversationalOnboardingProvider'
    );
  }

  return context;
};

export default ConversationalOnboardingContext;
